// import FormField to handle address/postcode input changes and form submission.
import { useGaslinkAddressSearch } from "../../hooks/useGaslinkAddressSearch";
import { GasAddressItem } from "../../types/services.types";
import ErrorBoundary from "../layout/ErrorBoundary";
import FormField from "../layout/FormField";

// Import a custom hook that encapsulates all address specific state, validation, and search logic.
// Import the GasMeterDetailsView component to display meter details.
import GasMeterDetailsView from "./MeterDetailsView";

const AddressSearch: React.FC = () => {
  const {
    postcodeInput,
    setPostcodeInput,
    houseNameInput,
    setHouseNameInput,
    houseNumberInput,
    setHouseNumberInput,
    error,
    addressResults,
    selectedAddress,
    meterDetails,
    isLoading,
    handleSearch,
    handleAddressSelection,
  } = useGaslinkAddressSearch();

  // Format addresses for display in the dropdown menu.
  // Unlike Electralink, Gas Meter Search API returns separate address fields that need to be combined.
  const formatAddress = (addr: GasAddressItem) => {
    const parts = [
      addr.house_number,
      addr.house_name,
      addr.street,
      addr.town,
      addr.postcode,
    ].filter((part) => part.trim());
    return parts.join(", ");
  };

  return (
    <div className="search-form">
      {/* Render the form input fields to allow the user to enter postcode, house name, and house number */}
      <FormField
        inputFields={[
          {
            id: "postcode",
            placeholder: "Enter a postcode (required)",
            value: postcodeInput,
            onChange: setPostcodeInput,
          },
          {
            id: "houseName",
            placeholder: "House Name (Optional)",
            value: houseNameInput,
            onChange: setHouseNameInput,
          },
          {
            id: "houseNumber",
            placeholder: "House Number (Optional)",
            value: houseNumberInput,
            onChange: setHouseNumberInput,
          },
        ]}
        onSubmit={handleSearch}
        disabled={isLoading}
      />

      {/* Display usage instructions to the user */}
      {!isLoading && (
        <div className="electralink-loading-message">
          If supplying a house name or number, please input <i>either</i> a name <i>or</i> a number,
          not both.
        </div>
      )}

      {/* In case of an error, display the error message on the UI. */}
      {error && <div className="error-message">{error}</div>}

      {/* Display a 'loading' indicator to provide UI feedback to users as they await the API response. */}
      {isLoading && <div className="electralink-loading-message">Loading...</div>}

      {/* If the addressResults exist, render a <select> dropdown to display the retrieved addresses */}
      {addressResults.length > 0 && !isLoading && (
        <div className="address-selection">
          <label htmlFor="gas-address-select">Select an address:</label>
          <select
            id="gas-address-select"
            onFocus={(e) =>
              (e.target.size = addressResults.length > 15 ? 15 : addressResults.length)
            }
            onBlurCapture={(e) => (e.target.size = 1)}
            value={selectedAddress}
            onChange={(e) => {
              void handleAddressSelection(e.target.value);
            }}
            className="form-select"
            disabled={isLoading}
          >
            {!meterDetails && <option value="">Select an address</option>}

            {addressResults.map((address, index) => (
              <option key={index} value={address.address_id}>
                {formatAddress(address)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Display full meter details in tiles if meter details exist. */}
      <ErrorBoundary level="section" label="these results" resetKey={meterDetails}>
        {meterDetails && !isLoading && <GasMeterDetailsView gasMeterDetails={meterDetails} />}
      </ErrorBoundary>
    </div>
  );
};

export default AddressSearch;
